import type { ReviewEvidence } from './review-evidence';
import axios from 'axios';
import type { PipelineCapacityMetadata } from './pipeline-capacity';
import { getApiUrl } from './config';
import type { CincoFrioEnvironmentalContext } from './cinco-frio-environmental';
import type { PipelineReference } from './pipeline-reference';
import type { OperatorCatalogEntry } from './operator-focus';

const api = axios.create({
  baseURL: getApiUrl(),
  withCredentials: false,
});

// Keep the reviewed-evidence response contract separate from older cached JSON.
const EVIDENCE_VERSION = '20261026r3';

// Auth: a /zones/<slug>?token=..., /cluster/<slug>?token=..., or
// /briefing/<id>[/v2]?token=... viewer attaches the share token as a query
// param (matches the API's ?token= pattern). Otherwise inject the stored
// Bearer password. Briefing share pages were missing here, so secondary
// fetches (e.g. the terrain-profile cross-section) went out unauthenticated
// and silently 401'd for share viewers.
const SHARE_PATH_RE = /^\/(?:zones|cluster)\/[a-z0-9-]+\/?$|^\/briefing\/[0-9a-f-]{36}(?:\/v2)?\/?$/i;
const VALID_SHARE_TOKEN_RE = /^[0-9a-f]{48}$/i;

api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    if (SHARE_PATH_RE.test(url.pathname)) {
      const shareToken = url.searchParams.get('token');
      if (shareToken && VALID_SHARE_TOKEN_RE.test(shareToken)) {
        config.params = { ...(config.params || {}), token: shareToken };
        return config;
      }
    }
  }
  const password = localStorage.getItem('pp-auth');
  if (password) {
    config.headers['Authorization'] = `Bearer ${password}`;
  }
  return config;
});

export interface Parcel {
  id: string;
  county: string;
  state: string;
  parcel_id_local: string | null;
  owner_name: string;
  mailing_address: string | null;
  property_address: string | null;
  acreage: number | null;
  land_use_code: string | null;
  improvement_value: number | null;
  land_value: number | null;
  total_appraised: number | null;
  is_absentee: boolean;
  acquisition_score: number;
  acquisition_land_readiness: number | null;
  acquisition_owner_signals: number | null;
  owner_parcel_count: number | null;
  land_use_category: string | null;
  is_government_owner: boolean | null;
  avg_slope_pct: number | null;
  max_slope_pct: number | null;
  elevation_range_ft: number | null;
  pct_under_2pct: number | null;
  pct_under_5pct: number | null;
  pct_under_10pct: number | null;
  pct_under_15pct: number | null;
  nearest_substation_miles: number | null;
  nearest_substation_kv: number | null;
  nearest_substation_name: string | null;
  nearest_substation_lines: number | null;
  second_substation_miles: number | null;
  second_substation_kv: number | null;
  second_substation_name: string | null;
  nearest_osm_switchyard_miles: number | null;
  nearest_osm_switchyard_name: string | null;
  nearest_osm_switchyard_voltage_kv: number | null;
  nearest_osm_switchyard_classifier: 'switchgear' | 'name_regex' | 'transmission' | null;
  nearest_osm_switchyard_hifld_backed: boolean | null;
  flood_zone: string | null;
  flood_zone_pct_100yr: number | null;
  nearest_highway_miles: number | null;
  nearest_highway_name: string | null;
  nearest_gas_plant_miles: number | null;
  nearest_railroad_miles: number | null;
  nearest_railroad_owner: string | null;
  pipeline_infra_score: number | null;
  dc_suitability_score: number | null;
  dc_score_version?: string | null;
  dc_score_basis?: 'reference_scenario' | 'retained' | 'mixed_or_unknown';
  pipeline_delivery_status?: 'unverified';
  pipeline_reference?: PipelineReference | null;
  fiber_available: boolean | null;
  fiber_provider_count: number | null;
  fiber_max_speed_mbps: number | null;
  fiber_has_enterprise: boolean | null;
  fiber_nearest_miles: number | null;
  water_service_area: boolean | null;
  aquifer_name: string | null;
  nearest_water_miles: number | null;
  water_regulatory_zone: string | null;
  water_regulatory_type: string | null;
  water_sourcing_tier: string | null;
  nearest_twdb_well_miles: number | null;
  nearby_water_wells?: { tracking_number: string; proposed_use: string; total_depth_ft: number | null; yield_gpm: number | null; drill_date: string | null; distance_miles: number }[];
  brackish_zones?: { aquifer_name: string | null; zone_type: string; tds_min: number | null; tds_max: number | null; area_sq_miles: number | null }[];
  nearby_tds_samples?: { tds_mgl: number; aquifer_name: string | null; depth_ft: number | null; sample_date: string | null; county: string | null; distance_miles: number }[];
  climate_station_id: string | null;
  climate_design_high_f: number | null;
  climate_annual_cdd: number | null;
  climate_station_dist_miles: number | null;
  nearest_commercial_airport_miles: number | null;
  nearest_commercial_airport_name: string | null;
  nearest_runway_5000ft_miles: number | null;
  nearest_runway_5000ft_name: string | null;
  nearest_runway_5000ft_length_ft: number | null;
  pipeline_proximate: boolean | null;
  assembly_group_id: string | null;
  gas_supply_mw: number | null;
  pipeline_available_mw: number | null;
  pipeline_available_name: string | null;
  pipeline_available_diameter: number | null;
  pipeline_available_local_source: string | null;
  pipeline_available_local_miles: number | string | null;
  pipeline_available_local_points: number | null;
  in_nonattainment: boolean | null;
  nonattainment_areas: Array<{ pollutant: string; classification: string | null; area_name: string | null }>;
  wetland_acres: number | null;
  wetland_pct: number | null;
  wetland_types: string | null;
  nearest_wetland_m: number | null;
  nearest_wetland_type: string | null;
  wildfire_risk_score: number | null;
  in_critical_habitat: boolean | null;
  critical_habitat_species: string | null;
  te_species_count: number | null;
  te_species_list: string | null;
  seismic_pga: number | null;
  nearest_urban_area_miles: number | null;
  nearest_urban_area_name: string | null;
  in_mine_subsidence_zone: boolean | null;
  nearest_mine_subsidence_miles: number | null;
  mine_subsidence_seam: string | null;
  in_aml_site: boolean | null;
  nearest_aml_miles: number | null;
  aml_severity: string | null;
  aml_problem_code: string | null;
  mine_overlap_acres: number | null;
  aml_overlap_acres: number | null;
  aml_overlaps?: AmlOverlap[];
}

export interface AmlOverlap {
  id: string | number;
  pad_name: string | null;
  pad_number: string | null;
  problem_code: string | null;
  severity: string | null;
  amlis_status: string | null;  // 'In AMLIS' | 'Not in AMLIS' | null
  status_raw: string | null;     // 'U' = unfunded (WVDEP code)
  date_updated: string | null;
  source_layer: string | null;   // 'AMLPAS' (current inventory) | 'AMLPolygons' (legacy)
  overlap_acres: number | null;
}

export interface NearbyPipeline extends PipelineCapacityMetadata {
  id: string;
  name: string;
  operator: string;
  distance_miles: number;
  distance_basis?: 'carve_out_boundary';
  capacity_mmcf_day: number | null;
  mw_potential: number | null;
  capacity_source: string | null;
  source?: string | null;             // e.g. 'EIA', 'RRC', 'WV DEP TAGIS'
  diameter_inches: number | null;
  utilization_pct: number | null;
  utilization_source: string | null;
  local_utilization_pct: number | null;
  local_util_source: string | null;
  flow_volume_mmcf_day: number | null;
  maop_psig: number | null;
  maop_source: string | null;
  system_type: string | null;
  pressure_grade: string | null;
  tap_lng?: number | null;
  tap_lat?: number | null;
  segment_count?: number;
  system_name?: string;
  // Friendly display fields (2026-07-27 overhaul): render-only. system_name/name
  // stay raw — they are exact-string join keys (headline pins, corridor slugs).
  system_display_name?: string | null;
  operator_parent?: string | null;
  display_name?: string | null;
  // Count of NULL-diameter siblings rolled up under this parent group. Surfaced as
  // a small data-gap dot in the UI so the row can stay visible (operator presence
  // matters for BTM dev) without polluting the capacity number.
  data_gap_segment_count?: number;
  // True when this row's underlying segments are gathering lines (`type = 'Natural Gas Gathering'`).
  // Gathering lines connect wells to the main pipeline — they are not BTM tap candidates
  // (a 200MW project would saturate any single gathering line). The UI hides these in a
  // separate collapsed "Gathering lines in corridor" section so they don't pollute the
  // tap-candidate list or the supply-redundancy count in the intro paragraph.
  is_gathering?: boolean;
  // True when this row's max diameter is below the small-transmission threshold (6").
  // These are technically transmission-classified per RRC but physically can't supply
  // a 200MW+ BTM project (4.5" Panhandle B ≈ 8 MMcf/d ≈ 40MW max). UI shows them as
  // tap candidates but with a "small transmission — operator confirmation needed" caveat
  // so the IC isn't misled by HR SAND-style 4.5" lines reading as primary tap options.
  is_small_transmission?: boolean;
  // True when this row matches the parcel's headline pipeline (set by
  // applyHeadlineMWOverride). UI uses this to badge the row and to swap in the
  // headline MW for the AVAILABLE column without clobbering gross capacity.
  headline_override?: boolean;
  // Curator-designated second featured row (structured_intel.co_headline_pipeline);
  // shares the Headline badge and a stamped mw_potential_available.
  co_headline?: boolean;
  // The parcel's precomputed pipeline_available_mw — this is the post-utilization-
  // haircut number, displayed in the AVAILABLE column for headline-matched rows.
  // Closes the Goff 3-way MW disagreement (gross capacity differs from this).
  mw_potential_available?: number | null;
  // The diameter recorded on the parcel headline (may differ from system parent's
  // MAX-derived diameter when the headline pinned a specific subsystem).
  headline_diameter_inches?: number | null;
  // Server dedups identical (subsystem, diameter) child segments into a single row
  // with a `segment_count` and optional `distance_range`. Stops EPNG expand from
  // showing 35 near-identical "LOOP LINE FROM KEYSTONE TO WINK" rows.
  segments?: (PipelineCapacityMetadata & { source?: string | null; mw_potential?: number | null; name: string; operator: string; distance_miles: number; distance_range?: { min: number; max: number } | null; diameter_inches: number | null; capacity_mmcf_day: number | null; capacity_source: string | null; maop_psig: number | null; utilization_pct: number | null; pressure_grade: string | null; subsystem?: string | null; segment_count?: number })[];
}

// Wraps the grouped pipeline list with metadata about the search radius so the UI
// can surface "Extended search (5–10 mi)" when no candidates passed the 5mi filter.
export interface NearbyPipelineResponse {
  pipelines: NearbyPipeline[];
  extendedSearch: boolean;
}

export interface PipelineObservation {
  id: string | number;
  pipelineCode: string | null;
  segmentNumber: string | number | null;
  segmentName: string | null;
  flowDirection: string | null;
  designCapacityDth: number | null;
  operatingCapacityDth: number | null;
  scheduledQuantityDth: number | null;
  availableCapacityDth: number | null;
  gasDay: string | null;
  cycle: string | null;
  source: string | null;
  sourceUrl: string | null;
  scheduledUtilizationPct: number | null;
}

export interface PipelineObservationsResponse {
  pipeline: { id: string; name: string; operator: string };
  coverage: {
    status: 'available' | 'no_data';
    pipelineCodes: string[];
    latestGasDays: string[];
    observationCount: number;
    scope: 'same_pipeline_system';
    observationMeaning: 'operator_posted_scheduled_capacity';
  };
  observations: PipelineObservation[];
}

export interface ColocatedOwner {
  id: string;
  parcel_id_local: string | null;
  owner_name: string;
  mailing_address: string | null;
}

export interface FiberProvider {
  name: string;
  download: number;
  upload: number;
  brc?: string;
}

export interface ParcelDetail extends Parcel {
  geojson?: GeoJSON.Geometry;
  lng: number;
  lat: number;
  tax_delinquent: boolean | null;
  nearby_pipelines?: NearbyPipeline[] | null;
  colocated_owners?: ColocatedOwner[];
  nearest_fiber_miles?: number | null;
  nearest_fiber_providers?: FiberProvider[] | null;
  nearest_fiber_max_download_mbps?: number | null;
  nearest_fiber_enterprise_count?: number | null;
  nearest_fiber_has_enterprise?: boolean | null;
  nearest_fiber_is_isolated?: boolean | null;
  nearest_carrier_fiber_miles?: number | null;
  nearest_carrier_fiber_name?: string | null;
  nearest_ixp_miles?: number | null;
  nearest_ixp_name?: string | null;
  nearest_ixp_networks?: number | null;
  pipeline_fiber_operators?: Array<{
    fiber_company: string;
    pipeline_operator: string;
    pipeline_name: string;
    diameter_inches: number;
    pipeline_type?: string | null;
    distance_miles: number;
    fiber_type: string;
    note: string;
  }> | null;
  carrier_fiber_nearest?: Array<{
    carrier: string;
    nearest_mi: number;
  }>;
  nearest_colo_facility?: string | null;
  nearest_colo_city?: string | null;
  nearest_colo_state?: string | null;
  nearest_colo_networks?: number | null;
  nearest_colo_miles?: number | null;
  ej_max_pctile?: number;
  ej_block_group_geoid?: string;
  ej_verification_url?: string;
  in_nonattainment?: boolean | null;
  nonattainment_areas?: Array<{ pollutant: string; classification: string; area_name: string }>;
  ej_detail?: {
    pctile_ej_pm25: number;
    pctile_ej_ozone: number;
    pctile_ej_no2: number;
    max_ej_indicator: string;
    demog_idx: number;
    pctile_demog: number;
    pm25: number;
    ozone: number;
    no2: number;
  };
  site_notes?: string | null;
  gas_supply_zone_slug?: string | null;
}

export interface Filters {
  county?: string;
  min_acreage?: number;
  max_acreage?: number;
  min_score?: number;
  max_score?: number;
  max_slope?: number;
  max_substation_miles?: number;
  min_substation_kv?: number;
  search?: string;
  pipeline_distance?: number;
  operators?: string;
  max_highway_miles?: number;
  min_dc_score?: number;
  max_dc_score?: number;
  fiber_enterprise?: boolean;
  min_fiber_providers?: number;
}

function filtersToParams(filters: Filters): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      params[key] = String(value);
    }
  }
  return params;
}

export function getParcelTileUrl(minAcreage?: number): string {
  const base = getApiUrl();
  const params = new URLSearchParams();
  if (minAcreage != null && minAcreage !== 200) params.set('min_acreage', String(minAcreage));
  const suffix = params.toString() ? `?${params}` : '';
  const origin = base.startsWith('/') ? window.location.origin + base : base;
  return `${origin}/parcels/tile/{z}/{x}/{y}.mvt${suffix}`;
}

export function getWetlandsTileUrl(): string {
  const base = getApiUrl();
  if (base.startsWith('/')) {
    return `${window.location.origin}${base}/wetlands/tile/{z}/{x}/{y}.mvt`;
  }
  return `${base}/wetlands/tile/{z}/{x}/{y}.mvt`;
}

export type TruncatedFeatureCollection = GeoJSON.FeatureCollection & { truncated?: boolean };

export type PipelineGroup = 'gathering' | 'gas_transmission' | 'liquids_transmission' | 'desert_sw';

export interface PipelineFilters {
  min_capacity?: number;
  min_mw?: number;
  capacity_source?: 'eia_rated' | 'diameter_estimate' | '';
  // Company focus ("only this company's lines"): the chosen company and its operator
  // names. Only `operators` goes to the server; `company` labels the focus chip.
  company?: string;
  operators?: string[];
}

export const fetchPipelinesGeoJSON = async (group?: PipelineGroup, bbox?: string, signal?: AbortSignal, zoom?: number, pipelineFilters?: PipelineFilters) => {
  const params: Record<string, string> = { capacity_semantics: '3' };
  if (group) params.group = group;
  if (bbox) params.bbox = bbox;
  if (zoom !== undefined) params.zoom = String(zoom);
  // Native delivery observations are not yet available for map filtering.
  // Do not send legacy inferred capacity thresholds, which would select stale
  // pipeline_available_mw claims as if they were verified delivery capacity.
  if (pipelineFilters?.capacity_source) params.capacity_source = pipelineFilters.capacity_source;
  // One JSON array in one key (the server keeps only the last of repeated keys); sorted so
  // the same focus always produces the same URL.
  if (pipelineFilters?.operators?.length) params.operators = JSON.stringify([...pipelineFilters.operators].sort());
  const res = await api.get('/pipelines/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

// Every distinct pipeline operator with its parent company — backs the
// Operators panel search + focus. ~1,450 rows, loaded once per app session.
export const fetchPipelineOperators = async (): Promise<OperatorCatalogEntry[]> => {
  const res = await api.get('/pipelines/operators');
  return (res.data as { operators: OperatorCatalogEntry[] }).operators;
};

const parcelCache = new Map<string, ParcelDetail>();

export const fetchParcelData = async (id: string): Promise<ParcelDetail> => {
  const cached = parcelCache.get(id);
  if (cached) return cached;
  const res = await api.get(`/parcels/${id}`);
  const data = res.data as ParcelDetail;
  parcelCache.set(id, data);
  return data;
};

export const fetchNearbyPipelines = async (id: string): Promise<NearbyPipelineResponse> => {
  const res = await api.get(`/parcels/${id}/nearby-pipelines`, { params: { capacity_semantics: '3' } });
  const data = res.data as NearbyPipelineResponse;
  return data;
};

export const fetchPipelineObservations = async (
  id: string,
  signal?: AbortSignal,
): Promise<PipelineObservationsResponse> => {
  const res = await api.get(`/pipeline-observations/${id}`, { signal });
  return res.data as PipelineObservationsResponse;
};

export interface NearestParcelResult {
  id: string;
  county: string;
  state: string;
  parcel_id_local: string | null;
  owner_name: string;
  acreage: number | null;
  lng: number;
  lat: number;
  distance_m?: number;
}

export const fetchNearestParcel = async (lat: number, lng: number): Promise<NearestParcelResult> => {
  const res = await api.get('/parcels/nearest', { params: { lat, lng } });
  return res.data as NearestParcelResult;
};

export const fetchParcelsList = async (filters: Filters = {}, limit = 100, offset = 0) => {
  const params = { ...filtersToParams(filters), limit: String(limit), offset: String(offset) };
  const res = await api.get('/parcels', { params });
  return res.data as { parcels: Parcel[]; has_more: boolean };
};

export const fetchFederalLandsGeoJSON = async (bbox?: string, signal?: AbortSignal, commitmentId?: string) => {
  const params: Record<string, string> = {};
  if (commitmentId) params.commitment_id = commitmentId;
  else if (bbox) params.bbox = bbox;
  const res = await api.get('/federal-lands/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

// Crown Pine's own LRM tract inventory (crownpine_tracts) — bbox + zoom for
// server-side simplification.
export const fetchCrownPineTractsGeoJSON = async (bbox?: string, zoom?: number, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  if (zoom != null) params.zoom = String(Math.round(zoom));
  const res = await api.get('/crownpine-parcels/tracts', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchAssemblyGroupsGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/assembly-groups/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export type PowerType = 'substation' | 'power_plant' | 'transmission_line';

export const fetchPowerGeoJSON = async (type?: PowerType, bbox?: string, signal?: AbortSignal, minVoltage?: number, includeOverlay?: boolean) => {
  const params: Record<string, string> = {};
  if (type) params.type = type;
  if (bbox) params.bbox = bbox;
  if (minVoltage != null) params.min_voltage = String(minVoltage);
  if (includeOverlay) params.include = 'overlay';
  const res = await api.get('/power/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchOsmSwitchyardsGeoJSON = async (
  opts: { bbox?: string; hifldNewOnly?: boolean; minVoltage?: number; classifier?: string[] } = {},
  signal?: AbortSignal,
) => {
  const params: Record<string, string> = {};
  if (opts.bbox) params.bbox = opts.bbox;
  if (opts.hifldNewOnly) params.hifld_new_only = '1';
  if (opts.minVoltage != null) params.min_voltage = String(opts.minVoltage);
  if (opts.classifier?.length) params.classifier = opts.classifier.join(',');
  const res = await api.get('/osm-switchyards/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchQueueHeatmapGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/power/queue-heatmap', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchCongestionHeatmapGeoJSON = async (signal?: AbortSignal) => {
  const res = await api.get('/power/congestion-heatmap', { signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchAirportsGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = { type: 'airport' };
  if (bbox) params.bbox = bbox;
  const res = await api.get('/power/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchFiberGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/fiber/geojson', { params, signal });
  return res.data as TruncatedFeatureCollection;
};

export const fetchCarrierRoutesGeoJSON = async (bbox?: string, signal?: AbortSignal, zoom?: number, carriers?: string[]) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  if (zoom !== undefined) params.zoom = String(zoom);
  if (carriers && carriers.length > 0) params.carriers = carriers.join(',');
  const res = await api.get('/carrier-routes/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export interface CarrierRouteStats {
  carrier: string;
  segments: number;
  total_miles: number;
  avg_segment_miles: string;
  source: string;
  latest_ingest: string;
}

export const fetchCarrierRouteStats = async (signal?: AbortSignal): Promise<CarrierRouteStats[]> => {
  const res = await api.get('/carrier-routes/stats', { signal });
  return res.data as CarrierRouteStats[];
};

export const fetchFiberRowPipelinesGeoJSON = async (bbox?: string, signal?: AbortSignal, zoom?: number) => {
  const params: Record<string, string> = { capacity_semantics: '3' };
  if (bbox) params.bbox = bbox;
  if (zoom !== undefined) params.zoom = String(zoom);
  const res = await api.get('/fiber-row-pipelines/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export interface PipelineFiberInfo {
  fiber_company: string;
  fiber_type: string;
  note: string;
}

export const fetchPipelineFiberLookup = async (operator: string, signal?: AbortSignal): Promise<PipelineFiberInfo | null> => {
  try {
    const res = await api.get('/fiber-row-pipelines/lookup', { params: { operator }, signal });
    return res.data as PipelineFiberInfo;
  } catch {
    // 404 = no fiber match for this operator, which is the common case
    return null;
  }
};

export const fetchIxpFacilitiesGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/ixp-facilities/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export type WaterType = 'well' | 'water_service_area';

export const fetchWaterGeoJSON = async (type?: WaterType, bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (type) params.type = type;
  if (bbox) params.bbox = bbox;
  const res = await api.get('/water/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchAquifersGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/water/aquifers/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchFloodZonesGeoJSON = async (bbox?: string, zoom?: number, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  if (zoom != null) params.zoom = String(Math.round(zoom));
  const res = await api.get('/flood-zones/geojson', { params: { ...params, fema_release: '2026-09-26' }, signal });
  return res.data as GeoJSON.FeatureCollection & { truncated?: boolean };
};

export const fetchPendingFloodZonesGeoJSON = async (bbox: string, zoom?: number, signal?: AbortSignal) => {
  const params: Record<string, string> = { bbox };
  if (zoom != null) params.zoom = String(Math.round(zoom));
  const res = await api.get('/flood-zones/pending/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection & { truncated?: boolean };
};

export const fetchTwdbFloodHazardsGeoJSON = async (bbox?: string, zoom?: number, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  if (zoom != null) params.zoom = String(Math.round(zoom));
  const res = await api.get('/twdb-flood-hazards/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchHighwaysGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/highways/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

// Roads layer — TxDOT Roadway Inventory segments plus TIGER primary/secondary and county
// roads. Coverage is deliberately narrow: La Salle County (inventory) and six East Texas
// counties (TIGER). Everywhere else the endpoint returns an empty collection.
export interface RoadFeatureProperties {
  id: string;
  kind: 'inventory' | 'prisec' | 'county';
  name: string | null;
  hwy: string | null;
  hsys: string | null;
  maintainer: 'TxDOT' | 'County' | 'City' | null;
  source: 'txdot_roadway_inventory' | 'tiger_prisec' | 'tiger_county';
  certified: boolean | null;
  mtfcc: string | null;
  lanes: number | null;
  surface_width_ft: number | null;
  surface_code: number | null;
  aadt: number | null;
  aadt_year: number | null;
  truck_pct: number | null;
  aadt_single_unit: number | null;
  aadt_combination: number | null;
  speed_limit_mph: number | null;
}

export const fetchRoadsGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/roads/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

// OSM pipeline candidates — the separately attributed, non-scoring overlay (migration 192).
// The collection carries `attribution` at the top level; the sidebar shows it while the layer is on.
export const fetchOsmPipelineCandidatesGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/osm-pipeline-candidates/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchRailroadsGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/railroads/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

// Gas supply / MW calculator types
export interface GasSupplyRadius {
  radius_miles: number;
  well_count: number;
  total_gas_mcf_day: number;
  flared_gas_mcf_day: number;
  associated_gas_pct: number | null;
  computed_at: string;
}

export interface GasContractReadiness {
  score: 'HIGH' | 'MEDIUM' | 'LOW';
  flare_pct: number;
  flared_mcf_day: number;
}

export interface GasSupplyResponse {
  has_data: boolean;
  radii: GasSupplyRadius[];
  supply_indicators: {
    associated_gas_pct: number | null;
    supply_reliability: string | null;
  };
  gas_contract_readiness?: GasContractReadiness;
}

const gasSupplyCache = new Map<string, GasSupplyResponse>();

export const fetchGasSupply = async (parcelId: string): Promise<GasSupplyResponse> => {
  const cached = gasSupplyCache.get(parcelId);
  if (cached) return cached;
  const res = await api.get(`/parcels/${parcelId}/gas-supply`);
  const data = res.data as GasSupplyResponse;
  gasSupplyCache.set(parcelId, data);
  return data;
};

export const fetchWellsGeoJSON = async (bbox?: string, state?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  if (state) params.state = state;
  const res = await api.get('/wells/geojson', { params, signal });
  return res.data as TruncatedFeatureCollection;
};

export const fetchFlaringHeatmap = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/wells/flaring-heatmap', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchHexGridGeoJSON = async (bbox: string, zoom: number, radius: number, signal?: AbortSignal) => {
  const params: Record<string, string> = { bbox, zoom: String(Math.round(zoom)), radius: String(radius) };
  const res = await api.get('/hex-grid/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

// Well detail types
export interface WellProduction {
  period: string;
  gas_prod_mcf: number | null;
  csgd_prod_mcf: number | null;
  oil_prod_bbl: number | null;
  gas_flared_mcf: number | null;
  gas_vented_mcf: number | null;
  csgd_flared_mcf: number | null;
  csgd_vented_mcf: number | null;
  gas_pipeline_mcf: number | null;
  gas_plant_mcf: number | null;
  gas_lease_use_mcf: number | null;
}

export interface WellDetail {
  id: number;
  api_number: string;
  well_name: string;
  operator: string;
  operator_id: string | null;
  well_type: string;
  well_status: string;
  county: string;
  state: string;
  latitude: number | null;
  longitude: number | null;
  depth_ft: number | null;
  spud_year: number | null;
  last_production_date: string | null;
  source: string;
  production_history: WellProduction[];
}

const wellCache = new Map<number, WellDetail>();

export const fetchWellData = async (id: number): Promise<WellDetail> => {
  const cached = wellCache.get(id);
  if (cached) return cached;
  const res = await api.get(`/wells/${id}`);
  const data = res.data as WellDetail;
  wellCache.set(id, data);
  return data;
};

export const fetchDataCentersGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = { evidence_v: EVIDENCE_VERSION };
  if (bbox) params.bbox = bbox;
  const res = await api.get('/data-centers/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchProtectedAreasGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/protected-areas/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchOperatorAoisGeoJSON = async (slug?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (slug) params.slug = slug;
  const res = await api.get('/operator-aois/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

// ───────────────── Supply Zone Briefings ─────────────────

export interface ZoneListEntry {
  slug: string;
  operator_name: string;
  asset_name: string;
  region_label: string;
  peak_volume_bcfd: number | null;
  peak_volume_year: number | null;
  dedicated_acres: number | null;
  archived: boolean;
}

export interface ZoneEligibleParcel {
  id: string;
  parcel_id_local: string | null;
  county: string;
  owner_name: string;
  owner_name_normalized: string | null;
  acres: number;
  effective_acres: number;
  effective_assembly_id: string | null;
  dc_suitability_score: number | null;
  pipeline_available_mw: number | null;
  pipeline_available_name: string | null;
  pipeline_available_diameter: number | null;
  fiber_provider_count: number | null;
  fiber_nearest_miles: number | null;
  fiber_has_enterprise: boolean | null;
  nearest_substation_miles: number | null;
  nearest_substation_kv: number | null;
  nearest_substation_name: string | null;
  nearest_highway_miles: number | null;
  nearest_highway_name: string | null;
  flood_zone: string | null;
  phase_bucket: 'phase_2' | 'phase_3' | 'inter_phase';
  aoi_dist_mi: number;
  pipeline_dist_mi: number;
  houston_dist_mi: number;
  composite_score: number;
  factor_aoi: number;
  factor_pipeline: number;
  factor_avail_mw: number;
  factor_houston: number;
  factor_suitability: number;
  centroid_lng: number;
  centroid_lat: number;
  polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon;
}

export interface ZoneOwnershipGroup {
  owner_key: string;
  owner_label: string;
  is_federal: boolean;
  parcels: number;
  total_acres: number;
  assemblies: number;
  counties: string[];
  parcel_ids: string[];
}

export interface ZoneSpreadCardTract {
  tract_id: string;        // assembly_id (UUID) for multi-parcel tracts, "parcel:<uuid>" for singles
  acres: number;
  owner: string | null;
  county: string | null;
  parcel_count: number;    // 1 for single-parcel tracts; >1 for assemblies
  is_assembly: boolean;    // true = multi-parcel contiguous assembly; false = single-parcel tract
}

export interface ZoneSpreadCardCombined {
  parcel_count: number;
  total_acres: number;
  assembly_count: number;  // count of multi-parcel assemblies only
  tract_count: number;     // total tracts (assemblies + single-parcel tracts)
  counties: string[];
  entities: string[];
  owner_label: string;
  top_tracts: ZoneSpreadCardTract[];
}

export interface ZoneSpreadCard {
  // family_1..family_5 = top owner families by deduped total acres in the AOI
  // (family_1 is always Crown Pine when present in the eligible pool).
  // Older bucket keys retained as a string union member for back-compat with
  // any cached client that still expects the old shape.
  bucket: 'family_1' | 'family_2' | 'family_3' | 'family_4' | 'family_5'
    | 'mega_assembly' | 'phase_2_a' | 'phase_2_b' | 'phase_3_a' | 'phase_3_b' | 'inter_phase_a';
  label: string;
  why_this_one: string;
  parcel: ZoneEligibleParcel;
  // All cards now carry a `combined` payload — each is rendered in
  // family-style with deduped total acres + top-5 contiguous assemblies.
  combined?: ZoneSpreadCardCombined;
}

export interface ZoneBriefing {
  commitment: {
    id: string;
    slug: string;
    operator_name: string;
    asset_name: string;
    region_label: string;
    peak_volume_bcfd: number | null;
    peak_volume_year: number | null;
    inventory_locations: number | null;
    inventory_years: number | null;
    dedicated_acres: number | null;
    ramp_curve: Array<{ date: string; phase_label: string; volume_mmcfd: number; cumulative_wells_tts: number }>;
    drill_schedule_notes: string | null;
    source_filename: string | null;
    source_date: string | null;
  };
  polygons: Array<{
    id: string;
    label: string;
    polygon_kind: 'aoi_outer' | 'phase_2' | 'phase_3';
    geometry: GeoJSON.Polygon;
    buffer_miles: number;
    sort_order: number;
  }>;
  aoi_buffer_geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  takeawayPipelines: Array<{
    id: string;
    name: string;
    operator: string;
    diameter_inches: number | null;
    system_type: string | null;
    capacity_mmcf_day: number | null;
    utilization_pct: number | null;
    local_utilization_pct: number | null;
    mw_potential: number | null;
    confidence: 'confirmed' | 'inferred_proximity' | 'inferred_operator';
    notes: string | null;
  }>;
  layers: {
    eligibleParcels: ZoneEligibleParcel[];
    substations: Array<{ id: string; name: string | null; voltage_kv: number | null; operator: string | null; lng: number; lat: number }>;
    gasPlants: Array<{ id: string; name: string | null; operator: string | null; plant_type: string | null; capacity_mmcf_d: number | null; lng: number; lat: number }>;
    ownershipRollup: ZoneOwnershipGroup[];
  };
  spread: ZoneSpreadCard[];
  aoi_pipelines: {
    summary: {
      total_named: number;
      pipelines_ge12in: number;
      pipelines_ge16in: number;
      pipelines_ge24in: number;
      total_effective_cap_mmcfd: number;
      total_headroom_mmcfd: number;
      total_headroom_mw: number;
      headroom_mw_ge12in: number;
      headroom_mw_ge16in: number;
      headroom_mw_ge24in: number;
      coverage: { ebb_local: number; ebb_zone: number; ebb_system: number; eia: number; default_assumed: number };
      mw_per_mmcf: number;
      panhandle_b: { ref_mmcfd: number; ref_dia_in: number; exponent: number };
      util_default_pct: number;
      segment_min_length_m: number;
    };
    tiers: Array<{
      key: string;
      label: string;
      named_pipelines: number;
      effective_cap_mmcfd: number;
      headroom_mw: number;
      ebb_local: number;
      ebb_zone: number;
      ebb_system: number;
      eia: number;
      default_assumed: number;
    }>;
    pipelines: Array<{
      name: string;
      display_name: string | null;
      operator: string;
      operator_parent: string | null;
      ebb_pipeline_code: string | null;
      diameter_in: number | null;
      system_cap_mmcfd: number | null;
      effective_cap_mmcfd: number | null;
      util_pct: number | null;
      util_basis: 'ebb_local' | 'ebb_zone' | 'ebb_system' | 'eia' | 'default';
      util_local_source: string | null;
      util_eia_source: string | null;
      util_local_nearest_miles: number | null;
      util_local_point_count: number | null;
      util_ebb_latest_gas_day: string | null;
      util_eia_latest_period: string | null;
      headroom_mmcfd: number | null;
      headroom_mw: number | null;
      segments: number;
      miles_in_aoi: number | null;
      pipe_type: string | null;
    }>;
  };
  pathway_to_6gw: {
    gas_takeaway_today_mmcfd: number;
    gas_takeaway_today_bcfd: number;
    gas_2031_bcfd: number | null;
    gas_2031_mw: number | null;
    midstream_status: string;
    midstream_text: string;
    anchor_assembly_id: string | null;
    anchor_assembly_acres: number;
    anchor_assembly_parcel_count: number;
    anchor_owner: string | null;
    anchor_county: string | null;
    assemblies_2k_count: number;
    assemblies_2k_acres: number;
    assemblies_2k_owners: number;
    assemblies_2k_families: number;
    assemblies_5k_count: number;
    assemblies_5k_acres: number;
    assemblies_5k_owners: number;
    assemblies_5k_families: number;
    assemblies_10k_count: number;
    assemblies_10k_acres: number;
    assemblies_10k_owners: number;
    assemblies_10k_families: number;
  };
  regional: {
    counties_in_aoi?: string[];
    parcel_count_in_aoi?: number;
  };
  meta: {
    houston: { lat: number; lng: number };
    weights: { aoi: number; pipeline: number; avail_mw: number; houston: number; suitability: number };
    falloffs_mi: { aoi: number; pipeline: number; houston: number };
    avail_mw: { gate_mw: number; saturation_mw: number };
    layers_default_visible: string[];
    eligible_count: number;
  };
}

export const fetchZoneList = async (signal?: AbortSignal): Promise<ZoneListEntry[]> => {
  const res = await api.get('/zones', { signal });
  return res.data as ZoneListEntry[];
};

export const fetchZoneBriefing = async (slug: string, signal?: AbortSignal): Promise<ZoneBriefing> => {
  const res = await api.get(`/zones/${encodeURIComponent(slug)}/briefing`, { signal });
  return res.data as ZoneBriefing;
};

export interface ZoneShareInfo {
  label: string | null;
  zone_slug: string;
}

export const fetchZoneShareInfo = async (signal?: AbortSignal): Promise<ZoneShareInfo> => {
  const res = await api.get('/zones/share/info', { signal });
  return res.data as ZoneShareInfo;
};

export const fetchElectricTerritoriesGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/electric-territories/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchStateTrustLandsGeoJSON = async (bbox?: string, state?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  if (state) params.state = state;
  const res = await api.get('/state-trust-lands/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchLandListingsGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = { evidence_v: EVIDENCE_VERSION };
  if (bbox) params.bbox = bbox;
  params.profile = 'corridor';
  const res = await api.get('/land-listings/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchGridListingsGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = { evidence_v: EVIDENCE_VERSION };
  if (bbox) params.bbox = bbox;
  params.profile = 'grid';
  const res = await api.get('/land-listings/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export interface SourceFreshness {
  last_updated: string | null;
  last_imported: string | null;
  last_reviewed: string | null;
  oldest_reviewed: string | null;
  last_source_check: string | null;
  reviewed_count: number;
  checked_count: number;
  total_count: number;
}

export const fetchLandListingsFreshness = async (): Promise<SourceFreshness> => {
  const res = await api.get('/land-listings/freshness', { params: { evidence_v: EVIDENCE_VERSION } });
  return res.data;
};

export const fetchDataCenterFreshness = async (): Promise<SourceFreshness> => {
  const res = await api.get('/data-centers/freshness', { params: { evidence_v: EVIDENCE_VERSION } });
  return res.data;
};

// County intelligence types
export interface CountyScore {
  state: string;
  county: string;
  gas_infrastructure_score: number;
  land_availability_score: number;
  acquisition_signals_score: number;
  site_quality_score: number;
  total_mw_potential: number | null;
  transmission_count: number;
  gathering_count: number;
  total_pipeline_count: number;
  total_capacity_mmcf_day: number | null;
  avg_utilization_pct: number | null;
  unique_operator_count: number;
  total_parcel_count: number;
  suitable_parcel_count: number;
  total_suitable_acreage: number;
  active_listing_count: number;
  avg_listing_price_per_acre: number | null;
  absentee_owner_count: number;
  absentee_owner_pct: number;
  entity_owner_count: number;
  tax_delinquent_count: number;
  portfolio_holder_count: number;
  prime_site_count: number;
  strong_site_count: number;
  avg_dc_suitability_score: number | null;
  total_pipeline_mw: number | null;
  best_pipeline_available_mw: number | null;
  fiber_available_pct: number;
  water_service_pct: number;
  avg_substation_miles: number | null;
  avg_highway_miles: number | null;
  bbox_west: number | null;
  bbox_south: number | null;
  bbox_east: number | null;
  bbox_north: number | null;
  has_parcel_data: boolean;
  computed_at: string | null;
}

export interface CountyDetailParcel {
  id: string;
  owner_name: string;
  county: string;
  acreage: number | null;
  dc_suitability_score: number | null;
  acquisition_score: number | null;
  pipeline_available_mw: number | null;
  land_use_category: string | null;
  is_absentee: boolean;
  fiber_available: boolean | null;
  water_service_area: boolean | null;
  nearest_substation_miles: number | null;
  nearest_highway_miles: number | null;
  lat: number;
  lng: number;
}

export interface CountyDetailPipeline {
  id: string;
  name: string;
  operator: string;
  type: string;
  capacity_mmcf_day: number | null;
  utilization_pct: number | null;
  mw_potential: number | null;
}

export interface CountyDetailListing {
  id: string;
  listing_title: string;
  listing_url: string;
  price: number | null;
  acreage: number | null;
  price_per_acre: number | null;
  seller_name: string | null;
}

export interface CountyDetailResponse {
  score: CountyScore;
  parcels: CountyDetailParcel[];
  pipelines: CountyDetailPipeline[];
  listings: CountyDetailListing[];
}

export const fetchCountyScores = async (): Promise<{ counties: CountyScore[]; computed_at: string | null }> => {
  const res = await api.get('/county-scores');
  return res.data;
};

export const fetchCountyDetail = async (state: string, county: string): Promise<CountyDetailResponse> => {
  const res = await api.get(`/county-scores/${encodeURIComponent(state)}/${encodeURIComponent(county)}`);
  return res.data;
};

// Assembly group types
export interface AssemblyGroup {
  id: string;
  owner_name: string;
  parcel_count: number;
  total_acreage: number;
  county: string | null;
  state: string;
  counties: string[];
  centroid_lat: number;
  centroid_lng: number;
  avg_willingness_score: number | null;
  pipeline_system_count: number | null;
  total_accessible_mw: number | null;
  best_pipeline_mw: number | null;
  best_pipeline_name: string | null;
  nearest_pipeline_miles: number | null;
  created_at: string;
}

export interface AssemblyDetailParcel {
  id: string;
  parcel_id_local: string | null;
  owner_name: string;
  acreage: number | null;
  dc_suitability_score: number | null;
  pipeline_available_mw: number | null;
  avg_slope_pct: number | null;
  fiber_available: boolean | null;
  flood_zone: string | null;
  epa_ozone_attainment: string | null;
  lat: number;
  lng: number;
}

export interface AssemblyDetailPipeline {
  id: string;
  name: string;
  operator: string;
  diameter_inches: number | null;
  capacity_mmcf_day: number | null;
  mw_potential: number | null;
  system_type: string;
  distance_miles: number;
}

export interface AssemblyDetailResponse {
  group: AssemblyGroup & {
    parcel_ids: string[];
    boundary_geojson: object;
  };
  parcels: AssemblyDetailParcel[];
  pipelines: AssemblyDetailPipeline[];
}

export const fetchAssemblyGroups = async (params?: {
  state?: string;
  min_acreage?: number;
  limit?: number;
}): Promise<{ assemblies: AssemblyGroup[]; total: number }> => {
  const res = await api.get('/assembly-groups', { params });
  return res.data;
};

export const fetchAssemblyDetail = async (id: string): Promise<AssemblyDetailResponse> => {
  const res = await api.get(`/assembly-groups/${id}`);
  return res.data;
};

export const fetchGasPlantsGeoJSON = async (signal?: AbortSignal) => {
  const res = await api.get('/gas-plants/geojson', { signal });
  return res.data as GeoJSON.FeatureCollection;
};

// ───────────────── Saved Locations ─────────────────

export type SavedLocationSourceType = 'parcel' | 'well';

export interface SavedLocation {
  id: number;
  name: string;
  lat: number;
  lng: number;
  note: string | null;
  source_type: SavedLocationSourceType | null;
  source_id: string | null;
  created_at: string;
}

export interface SaveLocationInput {
  name: string;
  lat: number;
  lng: number;
  note?: string | null;
  source_type?: SavedLocationSourceType | null;
  source_id?: string | null;
}

export const fetchSavedLocations = async (signal?: AbortSignal): Promise<SavedLocation[]> => {
  const res = await api.get('/saved-locations', { signal });
  return (res.data.locations || []) as SavedLocation[];
};

export const createSavedLocation = async (input: SaveLocationInput): Promise<SavedLocation> => {
  const res = await api.post('/saved-locations', input);
  return res.data as SavedLocation;
};

export const updateSavedLocation = async (
  id: number,
  patch: { name?: string; note?: string | null }
): Promise<SavedLocation> => {
  const res = await api.patch(`/saved-locations/${id}`, patch);
  return res.data as SavedLocation;
};

export const deleteSavedLocation = async (id: number): Promise<void> => {
  await api.delete(`/saved-locations/${id}`);
};

export const fetchGasInfraGeoJSON = async (type?: string, bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (type) params.type = type;
  if (bbox) params.bbox = bbox;
  const res = await api.get('/gas-infrastructure/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export interface ConnectedFacility {
  id: string;
  type: string;
  name: string | null;
  operator: string | null;
  pipeline_company: string | null;
  capacity_dth_day: number | null;
  horsepower: number | null;
  num_units: number | null;
  facility_type: string | null;
  status: string | null;
  state: string | null;
  county: string | null;
  lat: number;
  lng: number;
}

export const fetchGasInfraByPipeline = async (pipelineId: string, signal?: AbortSignal): Promise<ConnectedFacility[]> => {
  const res = await api.get('/gas-infrastructure/by-pipeline', { params: { pipeline_id: pipelineId }, signal });
  return res.data as ConnectedFacility[];
};

export const fetchEpaZonesGeoJSON = async (bbox?: string, zoom?: number, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  if (zoom != null) params.zoom = String(zoom);
  const res = await api.get('/epa-zones/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchEjScreenGeoJSON = async (bbox?: string, zoom?: number, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  if (zoom != null) params.zoom = String(zoom);
  const res = await api.get('/ejscreen/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchCriticalHabitatGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/critical-habitat/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchMineSubsidenceGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/mine-hazards/subsidence/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchAmlSitesGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/mine-hazards/aml/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchSeismicRiskGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/seismic-risk/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchUrbanAreasGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/urban-areas/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchWetlandsGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/wetlands/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection & { totalCount?: number };
};

export const fetchWildfireGeoJSON = async (bbox?: string, zoom?: number, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  if (zoom != null) params.zoom = String(zoom);
  const res = await api.get('/wildfire/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchTwdbWellsGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/water/twdb-wells/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchWaterRegulatoryZonesGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/water/regulatory/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchBrackishZonesGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/water/brackish/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

export const fetchTdsSamplesGeoJSON = async (bbox?: string, signal?: AbortSignal) => {
  const params: Record<string, string> = {};
  if (bbox) params.bbox = bbox;
  const res = await api.get('/water/tds-samples/geojson', { params, signal });
  return res.data as GeoJSON.FeatureCollection;
};

// Panel enrichment types
export interface PanelEnrichment {
  assembly: { id: string; owner_name: string; parcel_count: number; total_acreage: number; county: string; state: string; counties: string[] } | null;
  carrierFiber: Array<{ carrier: string; nearest_mi: number }>;
  pipelineFiber: Array<{ fiber_company: string; pipeline_operator: string; pipeline_name: string; diameter_inches: number | null; distance_miles: number; fiber_type: string; note: string }>;
  gasInfra: {
    compressors: Array<{ name: string; operator: string; pipeline_company: string; horsepower: number | null; distance_miles: number }>;
    receiptDelivery: Array<{ name: string; pipeline_company: string; capacity_dth_day: number | null; facility_type: string; distance_miles: number }>;
    interconnects: Array<{ name: string; pipeline_company: string; distance_miles: number }>;
  };
  powerInfra: {
    tduName: string | null;
    tduHoldingCompany: string | null;
    plannedTransmission: Array<{ title: string; voltage_kv: number; owner: string; in_service: string; status: string; tier: string }>;
    queueCongestion: { active_projects: number; pending_mw: number; avg_queue_months: number | null } | null;
    lmpZone: { location: string; avg_lmp: number; p95_lmp: number; hours_negative: number } | null;
    generators: Array<{ fuel_type: string; count: number; total_mw: number; retiring_mw: number; earliest_retirement: number | null }>;
  };
  waterWells: Array<{ tracking_number: string; proposed_use: string; total_depth_ft: number | null; yield_gpm: number | null; drill_date: string | null; distance_miles: number }>;
  gasSupplyZone?: GasSupplyZoneCommitment | null;
  carveOuts?: OperatorCarveOut[];
  carveOutAggregate?: CarveOutAggregate | null;
}

const enrichmentCache = new Map<string, PanelEnrichment>();

export const fetchPanelEnrichment = async (parcelId: string): Promise<PanelEnrichment> => {
  const cached = enrichmentCache.get(parcelId);
  if (cached) return cached;
  const res = await api.get(`/parcels/${parcelId}/panel-enrichment`);
  const data = res.data as PanelEnrichment;
  enrichmentCache.set(parcelId, data);
  return data;
};

// Briefing types
export interface ParcelGrade {
  grade: string;
  score: number;
  percentile: number;
}

export interface BriefingGrades {
  fuel_delivery: ParcelGrade;
  site_readiness: ParcelGrade;
  // Absent on partner-redacted payloads — the server drops the key so the
  // Acquisition card, which is built out of price and valuation, never renders.
  acquisition?: ParcelGrade;
  region: string;
  total_parcels: number;
}

export interface BriefingListing {
  listing_title: string | null;
  price: number | null;
  acreage: number | null;
  seller_name: string | null;
  listing_url: string | null;
  landwatch_url: string | null;
  address: string | null;
  county: string | null;
  state: string;
  dsw_distance_miles: number | null;
  in_dsw_corridor: boolean;
  distance_miles: number;
}

interface MeinersCandidateProperties {
  label: string;
  acres: number;
  usable_at_1000_acres: number;
  is_fully_clear_at_1000ft: boolean;
  name?: string;
  candidate_kind?: string;
  sections?: string[];
  width_ft?: number;
  height_ft?: number;
  rotation_deg?: number;
  fit_basis?: string;
  verified_inside_500_residual?: boolean;
  exclusion_overlap_acres?: number;
  legacy_plugged_or_dry_wellheads_inside?: number;
  mapped_road_crossings?: number;
  gathering_line_crossings?: number;
  nearest_fm469_frontage_ft?: number;
  nearest_mapped_ranch_road_ft?: number;
  access_distance_basis?: string;
  usable_at_1000_pct?: number;
  alternative_not_simultaneous?: boolean;
}

export interface MeinersFootprintData {
  scenarios: Array<{
    pad_buffer_ft: number;
    row_buffer_each_side_ft: number;
    residual_acres: number;
    largest_contiguous_acres: number;
    piece_count?: number;
    geometry: GeoJSON.FeatureCollection;
    exclusions: {
      active_pad_buffers: GeoJSON.FeatureCollection;
      gathering_row: GeoJSON.FeatureCollection;
    };
  }>;
  candidates: GeoJSON.FeatureCollection<GeoJSON.Polygon, MeinersCandidateProperties>;
  boundary: GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
  flood: GeoJSON.FeatureCollection;
  wells?: GeoJSON.FeatureCollection<GeoJSON.Point>;
}

export interface BriefingMapLayers {
  // Present only on the Crown Pine A1 carve-out view — the site's western band,
  // part of the premises rather than an overlay. User-toggleable, default on.
  siteBand?: GeoJSON.FeatureCollection;
  // Present only on the Crown Pine A1 carve-out view — seller-proposed alternative
  // site layouts (KMZ outlines), one feature per layout. Properties: key, name,
  // label, acres, overlap_ac, added_ac, dropped_ac, texoma_crossing_mi, source.
  // Map outlines only: nothing scored reads them — every grade, gate and
  // terrain figure stays on the analyzed core.
  siteAlternatives?: GeoJSON.FeatureCollection;
  // Same edition: the ghost tracts where each alternative differs from the
  // original — polygon features (role 'area') plus a label point per piece
  // (role 'label'); properties: parent (alt key), kind 'added' | 'dropped',
  // acres, upland_ac, under5_upland_ac, label.
  siteAlternativePieces?: GeoJSON.FeatureCollection;
  // Same edition: best clean pad rectangles in the west and east ground of
  // each layout (rotation-optimized, avoiding streams / wetlands / 100-yr
  // flood) — polygons (role 'area') + label points (role 'label'); properties:
  // layout, side, rank, acres, texoma_mi, slope stats, label.
  sitePads?: GeoJSON.FeatureCollection;
  // Present only when structured_intel.map_add_on designates an optional add-on
  // tract (e.g. Cinco Frio's Boeck) — ghost-outlined on the briefing map.
  addOnTract?: GeoJSON.FeatureCollection;
  pipelinesGas: GeoJSON.FeatureCollection;
  pipelinesDesertSw: GeoJSON.FeatureCollection;
  pipelinesGathering: GeoJSON.FeatureCollection;
  substations: GeoJSON.FeatureCollection;
  highways: GeoJSON.FeatureCollection;
  gasPlants: GeoJSON.FeatureCollection;
  railroads: GeoJSON.FeatureCollection;
  fiberCoverage: GeoJSON.FeatureCollection;
  aquifers: GeoJSON.FeatureCollection;
  floodZones: GeoJSON.FeatureCollection & { truncated?: boolean };
  // Present only when the parcel has no FEMA determination and TWDB screening
  // hazards were merged into floodZones (targeted counties: Frio + 20 ETX).
  floodZonesSource?: 'twdb_screening';
  airports: GeoJSON.FeatureCollection;
  carrierRoutes: GeoJSON.FeatureCollection;
  osmSwitchyards: GeoJSON.FeatureCollection;
  waterWells: GeoJSON.FeatureCollection;
  dataCenters: GeoJSON.FeatureCollection;
  wetlands: GeoJSON.FeatureCollection;
  compressors: GeoJSON.FeatureCollection;
  // Present only when an oil & gas well screen (parcel_proximity layer
  // 'oil_gas_wells_screen') exists for the parcel — RRC well surface locations
  // (properties: status, operator, symbol, inside, section, field, …) and the
  // surface→bottom-hole lateral paths of the horizontal wells.
  oilGasWells?: GeoJSON.FeatureCollection;
  oilGasLaterals?: GeoJSON.FeatureCollection;
  // Present only when a road-access screen (parcel_proximity layer 'road_access_screen')
  // exists for the parcel — the heavy-haul line from the interstate interchange with the
  // structure / gate / entrance points on the same source (properties.kind = 'route' |
  // 'structure' | 'gate' | 'entrance'), and the public frontage segments plus the ranch
  // tracks inside the fence (properties.kind = 'frontage' | 'track').
  accessRoute?: GeoJSON.FeatureCollection;
  frontageRoads?: GeoJSON.FeatureCollection;
  // Present only when structured_intel.map_public_roads is set (Meiners Ranch) — the TxDOT
  // Roadway Inventory segments in the map box (properties: id, name, hsys, system,
  // maintainer, lanes, surface_width_ft, surface_code, aadt, aadt_year, truck_pct, …).
  publicRoads?: GeoJSON.FeatureCollection;
}

// One RRC well from the targeted oil & gas screen (RRC GIS layers 1/9/10 joined to the
// EWA wellbore + W-1 permit records). `inside` = surface location inside the carve-out
// (or parcel); the rest sit inside the screen's buffer for context.
export interface OilGasWellRecord {
  api: string; api_short: string; symbol: string;
  status: 'producing' | 'plugged' | 'permitted' | 'dry_hole' | 'shut_in' | 'injection' | 'other';
  enverusActivityStatus?: 'active' | 'inactive';
  enverusLastProductionMonth?: string | null;
  inside: boolean; distance_mi: number; section: string | null; lng: number; lat: number;
  operator: string | null; operator_basis: string | null;
  lease_name: string | null; lease_no: string | null; well_no: string | null; field: string | null;
  on_schedule: string | null; api_depth_ft: number | null;
  permit_no: string | null; permit_approved: string | null; permit_submitted: string | null; permit_status: string | null;
  wellbore_profile: string | null; filing_purpose: string | null; permits_count: number;
  horizontal: boolean; lateral_length_ft: number | null; lateral_bearing_deg: number | null;
  bottom_hole: { lng: number; lat: number; inside: boolean | null } | null;
  lateral_path: number[][] | null;
}

export interface OilGasWellsScreen {
  scope: { basis: string; buffer_mi: number; fetched_at: string };
  sources: { locations: string; laterals: string; wellbore: string; permits: string; note: string };
  summary: {
    inside_total: number; inside_producing: number; inside_plugged: number; inside_shut_in: number;
    inside_dry_hole: number; inside_permitted: number; inside_horizontal: number; inside_pads: number;
    buffer_total: number; buffer_producing: number;
    operators: Array<{ name: string; wells_inside: number }>;
    permits_active_recent: number; permits_stale: number;
    by_section: Array<{ section: string; wells: number; producing: number; plugged: number; permitted: number }>;
    permit_year_range: [number, number] | null;
    bottom_holes_inside_from_outside: number;
  };
  wells: OilGasWellRecord[];
}

// Road-access screen (data/manual/2026-09-09-meiners-road-access/screen-road-access.js):
// TxDOT Roadway Inventory segments along the block boundary, the shortest network path from
// the interstate interchange to the first frontage point, TxDOT NBI structures on that path,
// and the OSM ranch tracks inside the fence. Geometry is served on mapLayers only.
export interface RoadAccessFrontage {
  objectid: number; route: string; hwy: string | null; system: string; system_label: string; roadbed: string | null;
  maintainer: string | null; functional_class: string | null;
  lanes: number | null; lane_width_ft: number | null; roadbed_width_ft: number | null; surface_width_ft: number | null;
  shoulder_width_ft: number | null; row_min_ft: number | null; speed_limit_mph: number | null;
  surface_code: number | null; surface: string;
  aadt: number | null; aadt_year: number | null; truck_pct: number | null; aadt_trucks: number | null;
  aadt_single_unit: number | null; aadt_combination: number | null;
  from_dfo: number | null; to_dfo: number | null; frontage_mi: number; side: string | null; sections: string[];
}
export interface RoadAccessSegment {
  objectid: number; route: string; system: string; system_label: string; roadbed: string | null; maintainer: string | null;
  from_dfo: number | null; to_dfo: number | null; miles: number;
  lanes: number | null; lane_width_ft: number | null; roadbed_width_ft: number | null; surface_width_ft: number | null; shoulder_width_ft: number | null;
  surface: string; speed_limit_mph: number | null; aadt: number | null; aadt_year: number | null; truck_pct: number | null;
  aadt_trucks: number | null; aadt_single_unit: number | null; aadt_combination: number | null; functional_class: string | null;
}
export interface RoadAccessStructure {
  nbi_id: string; facility: string; feature_crossed: string; location: string; type: 'bridge' | 'culvert';
  year_built: number | null; year_reconstructed: number | null; design_load: string | null;
  posted: boolean; posted_load_klbs: string | null; posting_label: string; posting_appraisal: string | null; operational_status: string | null;
  min_vert_clear_on_ft: number | null; min_vert_clear_under_ft: number | null; inventory_rating_tons: number | null;
  structure_length_ft: number | null; roadway_width_ft: number | null; spans: number | null; max_span_ft: number | null;
  condition_class: string | null; inspected: string | null; aadt: number | null; aadt_year: number | null;
  on_route: boolean; on_frontage: boolean; at_interchange: boolean; mi_from_interchange: number; lng: number; lat: number;
}
export interface RoadAccessTrack {
  osm_id: string | number; highway: string; kind: string; name: string | null; ref: string | null; surface: string | null;
  access: string | null; tracktype: string | null; inside_mi: number; total_mi: number; sections: string[]; touches_boundary: boolean;
  tie_in: { route: string; system: string; distance_ft: number; lng: number; lat: number } | null;
}
export interface RoadAccessScreen {
  scope: { basis: string; sections: string[]; frontage_tolerance_m: number; fetched_at: string; county_txdot_code: number; route: string; interchange: string };
  sources: {
    roadway_inventory: { url: string; where: string; fetched_at: string; note: string };
    traffic_stations: { url: string; fetched_at: string; note: string };
    bridges: { url: string; snbi_url: string; fetched_at: string; note: string };
    osm: { url: string; bbox: string; fetched_at: string; note: string };
    note: string;
  };
  interchange: { name: string; lng: number; lat: number };
  gate: { lng: number; lat: number; section: string | null; basis: string };
  gate_candidates: Array<{ route: string; lng: number; lat: number; via_osm_id: string | number; via: string; section: string | null; network_mi_from_interchange: number | null; straight_mi_from_interchange: number }>;
  // Nearest TxDOT count station on the route, with its 20-year count history. Null when none sits on the route.
  traffic_station: { id: string; on_road: string; lng: number; lat: number; distance_from_route_ft: number; on_frontage: boolean; latest_year: number; latest_adt: number; peak_year: number; peak_adt: number; series: Array<{ year: number; adt: number }>; source: string } | null;
  nearby_public_roads: Array<{ route: string; system: string; system_label: string; maintainer: string | null; min_distance_ft: number; min_distance_mi: number }>;
  summary: {
    frontage_routes: Array<{ route: string; system: string; system_label: string; maintainer: string | null; frontage_mi: number; sides: string[]; sections: string[] }>;
    frontage_total_mi: number;
    public_roads_inside: Array<{ route: string; system: string; maintainer: string | null; inside_mi: number }>;
    haul_route_mi: number; haul_route_straight_mi: number; haul_route_from: string; haul_route_to: string; haul_route_segments: number;
    haul_route_min_lanes: number; haul_route_min_surface_width_ft: number; haul_route_surfaces: string[];
    haul_route_aadt_range: [number, number]; haul_route_truck_pct_max: number;
    structures_on_route: number; structures_on_frontage: number; structures_posted: number; interchange_min_clearance_under_ft: number | null;
    traffic_station_id: string | null; traffic_latest_year: number | null; traffic_latest_adt: number | null; traffic_peak_year: number | null; traffic_peak_adt: number | null;
    tracks_inside: number; tracks_inside_mi: number; tracks_with_tie_in: number; gate_candidates: number;
  };
  frontage: RoadAccessFrontage[];
  haul_route: { network_mi: number; straight_mi: number; segments: RoadAccessSegment[] };
  structures: RoadAccessStructure[];
  tracks: RoadAccessTrack[];
}

// Road-access copy helpers (Matthew's wording rule, 2026-09-09: short, plain, one traffic line).
// TxDOT's pavement-type label → the words a reader uses; the inches stay.
export function roadSurfaceShort(label: string | null | undefined): string | null {
  if (!label) return null;
  const l = label.toLowerCase();
  if (l.includes('thick')) return 'thick asphalt (over 5.5 in)';
  if (l.includes('intermediate')) return 'asphalt (2.5–5.5 in)';
  if (l.includes('thin asphalt') || l.includes('asphaltic concrete, thin')) return 'thin asphalt (under 2.5 in)';
  if (l.includes('surface-treated') || l.includes('chip seal')) return 'chip seal';
  if (l.includes('composite')) return 'asphalt over concrete';
  if (l.includes('concrete')) return 'concrete';
  if (l.includes('overlaid')) return 'overlaid pavement';
  if (l.includes('not recorded')) return null;
  return label;
}
// TxDOT pavement-type code (SRF_TYPE) → the label roadSurfaceShort() knows how to shorten.
export function roadSurfaceFromCode(code: number | null | undefined): string | null {
  if (code == null || !Number.isFinite(code)) return null;
  if (code >= 1 && code <= 3) return 'concrete';
  if (code === 4) return 'Asphaltic concrete, thick (> 5.5 in)';
  if (code === 5) return 'Asphaltic concrete, intermediate (2.5–5.5 in)';
  if (code === 6) return 'Asphaltic concrete, thin (< 2.5 in)';
  if (code === 7) return 'Composite (asphalt over concrete)';
  if (code === 10) return 'Surface-treated flexible base (chip seal)';
  return null;
}
// "a third of it trucks" — the nearest plain fraction for a truck share.
export function truckShareWords(pct: number | null | undefined): string | null {
  if (pct == null || !Number.isFinite(pct)) return null;
  const f = pct / 100;
  const opts: Array<[number, string]> = [[0.5, 'half of it trucks'], [1 / 3, 'a third of it trucks'], [0.25, 'a quarter of it trucks'], [0.2, 'a fifth of it trucks'], [0.1, 'a tenth of it trucks'], [0.05, 'a few trucks']];
  if (f > 0.6) return 'mostly trucks';
  if (f < 0.03) return 'almost no trucks';
  return opts.reduce((best, o) => Math.abs(o[0] - f) < Math.abs(best[0] - f) ? o : best)[1];
}
// 14.58 → "14 ft 7 in"
export function feetInches(ft: number | null | undefined): string | null {
  if (ft == null || !Number.isFinite(ft)) return null;
  const whole = Math.floor(ft); const inches = Math.round((ft - whole) * 12);
  return inches === 12 ? `${whole + 1} ft` : inches === 0 ? `${whole} ft` : `${whole} ft ${inches} in`;
}
// A one-word read on a daily count.
export function trafficWord(adt: number | null | undefined): string | null {
  if (adt == null || !Number.isFinite(adt)) return null;
  return adt < 1000 ? 'quiet road' : adt < 5000 ? 'light traffic' : adt < 20000 ? 'busy road' : 'major corridor';
}

export interface NearestFeature {
  name: string;
  operator?: string;
  max_voltage_kv?: number;
  faa_id?: string;
  airport_class?: string;
  runway_max_paved_ft?: number | null;
  runway_data?: Array<{ id?: string | null; len_ft?: number; width_ft?: number | null; surface?: string | null; lit?: boolean; cond?: string | null }> | null;
  lng: number;
  lat: number;
  distance_miles: number;
}

// One row of the A1 site-layout table: the original premises or a proposed
// alternative. Presentation figures — nothing scored reads them. The optional
// fields exist only on the alternatives and drive the layout-aware lines on the
// A1 Alt page (flood note, elevation) while that outline is on the map.
export interface SiteLayoutRow {
  key: string; name: string; acres: number; acres_basis: string;
  // Alt3 uses Crown Pine's stated tract acreage as the headline while retaining
  // the fitted polygon acreage as the spatial-calculation reference.
  measured_acres?: number; serialized_geometry_acres?: number;
  flood_100yr_ac: number; nwi_wetland_ac: number; upland_ac: number;
  slope_mean_pct_upland: number | null; slope_under5_upland_ac: number | null; slope_under10_upland_ac: number | null;
  texoma_crossing_mi: number; added_ac: number; dropped_ac: number; parcels_note: string;
  // 'seller' (Alternatives #1, #2) or 'buyer' (Alternative #3, the counter-proposal). Original: absent.
  proposer?: 'seller' | 'buyer';
  pads_status?: 'illustrative'; pads_note?: string;
  terrain_source_qualification?: 'qualified_remote' | 'local_prototype';
  terrain_status?: 'withheld' | 'reference'; terrain_note?: string; terrain_reference_upland_ac?: number;
  flood_note?: string;
  slope_under2_upland_ac?: number | null; slope_under15_upland_ac?: number | null;
  // Slope figures above are the 3 m grid; these are the 5 m end of the range.
  slope_mean_pct_upland_5m?: number | null; slope_under2_upland_ac_5m?: number | null; slope_under5_upland_ac_5m?: number | null;
  slope_under10_upland_ac_5m?: number | null; slope_under15_upland_ac_5m?: number | null;
  elev_min_ft?: number | null; elev_max_ft?: number | null;
  elev_range_ft_gross?: number | null; elev_range_ft_upland?: number | null;
  elev_p5_ft_upland?: number | null; elev_p95_ft_upland?: number | null;
}

// Committed slope file for a briefing we build (api-server/data/briefing-slope/*.json):
// USGS 1 m lidar averaged to the 3 m and 5 m grids. Present only on briefings that have one.
export interface SlopeGridFigures {
  mean_slope_pct: number; pct_under_2: number; pct_under_5: number; pct_under_10: number; pct_under_15: number;
  acres_under_2: number; acres_under_5: number; acres_under_10: number; acres_under_15: number;
}
export interface SlopeGrids {
  schema_version: 1; basis: string; primary: string;
  source: { status: 'lidar_1m' | 'no_lidar'; label: string; published: string | null; tiles: string[] };
  footprints: { key: string; label: string; acres: number; raster_acres?: number; grids?: { '3m': SlopeGridFigures; '5m': SlopeGridFigures } }[];
}

export interface BriefingData {
  parcel: ParcelDetail;
  environmentalContext?: CincoFrioEnvironmentalContext;
  meinersFootprint?: MeinersFootprintData;
  pipelines: NearbyPipeline[];
  pipelinesDistanceBasis?: {
    type: 'carve_out_boundary';
    label: string;
    carveOutSlug: string;
    parcelCount: number;
  };
  pipelinesExtendedSearch?: boolean;
  pipelinesSearchRadiusMi?: number | null;
  // Present only when a targeted RRC oil & gas well screen exists for the parcel.
  oilGasWells?: OilGasWellsScreen;
  // Present only when a road-access screen exists for the parcel.
  roadAccess?: RoadAccessScreen;
  gasSupply: GasSupplyResponse;
  grades: BriefingGrades;
  listings: BriefingListing[];
  mapLayers: BriefingMapLayers;
  nearestFeatures?: {
    gasPlant: NearestFeature | null;
    railroad: NearestFeature | null;
    substation: NearestFeature | null;
    secondSubstation: NearestFeature | null;
    airport: NearestFeature | null;
    majorAirport: NearestFeature | null;
    nearestCommercial: NearestFeature | null;
    pavedRunway: NearestFeature | null;
    altPavedRunway: NearestFeature | null;
    altPavedRunway2: NearestFeature | null;
  };
  powerInfra?: {
    powerPlants: Array<{ name: string; operator: string; capacity_mw: number | null; fuel_source: string; status: string; distance_miles: number }>;
    transmissionLines: Array<{ name: string; voltage_kv: number | null; operator: string; volt_class: string; distance_miles: number }>;
    totalInstalledMW: number;
    tduName: string | null;
    tduHoldingCompany: string | null;
    generators: Array<{
      fuel_type: string;
      count: number;
      total_mw: number;
      retiring_mw: number;
      earliest_retirement: number | null;
      tdu_owner: string | null;
      max_grid_kv: number | null;
    }>;
  };
  gasInfrastructure?: {
    compressors: Array<{ type: string; name: string; operator: string; pipeline_company: string; horsepower: number | null; num_units: number | null; distance_miles: number }>;
    receipt_delivery: Array<{ type: string; name: string; pipeline_company: string; capacity_dth_day: number | null; facility_type: string; distance_miles: number }>;
    interconnects: Array<{ type: string; name: string; pipeline_company: string; connecting_companies: string[] | null; distance_miles: number }>;
    storage: Array<{ type: string; name: string; operator: string; capacity_dth_day: number | null; facility_type: string; distance_miles: number }>;
  };
  pipelineSafety?: {
    total_incidents: number;
    serious: number;
    significant: number;
    total_fatalities: number;
    total_injuries: number;
    years_covered: string;
  };
  countyZoning?: { zoning_status: string; zoning_notes: string | null } | null;
  airQualityHeadroom?: Array<{
    pollutant: string;
    design_value: number;
    design_value_units: string;
    naaqs_threshold: number;
    headroom: number;
    headroom_pct: number;
    monitor_count: number;
    standard: string;
  }>;
  nearbyDataCenters?: Array<{
    id?: string;
    review?: ReviewEvidence | null;
    name: string;
    operator: string;
    status: string;
    capacity_mw: number | null;
    city: string | null;
    county: string | null;
    state: string;
    year_announced: number | null;
    year_opened: number | null;
    nearest_pipeline_name: string | null;
    nearest_pipeline_distance_mi: number | null;
    distance_miles: number;
  }>;
  freshness: { data_as_of: string; status: 'current' | 'aging' | 'stale'; gas_infrastructure?: string; pipeline_incidents?: string };
  gasSupplyZone?: GasSupplyZoneCommitment | null;
  carveOuts?: OperatorCarveOut[];
  carveOutAggregate?: CarveOutAggregate | null;
  // Crown Pine A1 only. The acquisition premises = the analyzed core plus the
  // western band. Presentation figures for the copy layer; nothing scored reads
  // them (grades, gates and slope/flood/water all run on the core, which stays
  // on carveOutAggregate).
  premises?: {
    total_acres: number;
    core_acres: number;
    band_acres: number;
    parcels: string;
    band_crossing_mi: number;
    // County-assessed value across the premises — NEVER the proposal price.
    // Nulled by the server on the partner edition; guard before rendering.
    assessed_value: number | null;
    assessed_per_ac: number | null;
  } | null;
  // Crown Pine A1 only. Text rows for the site-layout table: the original
  // premises vs each seller-proposed alternative. Presentation only.
  siteLayouts?: {
    basis: string;
    rows: SiteLayoutRow[];
  } | null;
  // Per-layout Site Intelligence text (A1 Alt only): `sections` replace the body
  // under the matching heading while that layout is on the map; `note` renders above it.
  siteLayoutNarratives?: Record<string, { sections?: Record<string, string>; note?: string }> | null;
  // The briefing's slope file, when it has one (.claude/rules/terrain-slope.md).
  slopeGrids?: SlopeGrids;
  // Grid lens — present ONLY when the briefing was fetched with ?profile=grid.
  gridScore?: GridScore | null;
  gridNodeIntel?: GridNodeIntel | null;
  developableLand?: DevelopableLand | null;
  redFlags?: RedFlag[];
  // Present only on in-app (non-token) responses — token payloads omit it entirely.
  gridLensAvailable?: boolean;
  // Set by the server when the share token carries redaction='partner'. The
  // price/valuation and client-identity data is already gone from this payload —
  // the flag only tells the client which copy to render in its place.
  redacted?: 'partner';
}

export type GridComponentTier = 'scored' | 'curated' | 'neutral' | 'provisional';

export interface GridScoreComponent {
  key: 'power' | 'utility' | 'firming' | 'site' | 'price' | 'incentives';
  label: string;
  points: number;
  max: number;
  tier: GridComponentTier;
}

export interface GridScore {
  total: number;
  max: number;
  rubric_version: string;
  components: GridScoreComponent[];
  curatedTotal: number;
  dataConfidence: 'provisional' | 'partial' | 'firm';
  anchor: {
    kv: number | null;
    miles: number | null;
    lines: number | null;
    backbone_345kv: { kv: number; miles: number | null } | null;
  };
}

export interface RedFlag {
  key: string;
  label: string;
  severity: 'red' | 'watch';
  detail: string;
}

export interface DevelopableLand {
  developable_acres: number | null;
  buildability_pct: number | null;
  premium_flat_pct: number | null;
  avg_slope_pct: number | null;
}

export interface GridNodeEvidenceFact {
  fact: string;
  source: string;
  date: string;
}

export interface GridNodeEvidenceFactor {
  score: number;
  rationale: string;
  facts: GridNodeEvidenceFact[];
}

// Curated site profile for the grid briefing's Site & Incentives panel. Sourced from
// the KY EconDev / One East Kentucky listing; lives in the node evidence jsonb.
export interface GridSiteProfile {
  property_id?: string;
  address?: string;
  owner?: string;
  zoning?: string;
  total_acres?: number;
  status?: string[];
  price?: string;
  incentives?: { label: string; note?: string }[];
  utility?: {
    electric?: string;
    gas?: string;
    water?: string;
    sewer?: string;
    broadband?: string;
  };
  transport?: string;
  source?: string;
  source_url?: string;
  as_of?: string;
}

export interface GridNodeIntel {
  node_name: string;
  display_name: string | null;
  state: string | null;
  county: string | null;
  radius_miles: number | string | null;
  utility_appetite_score: number | string | null;
  power_price_score: number | string | null;
  incentives_community_score: number | string | null;
  evidence: {
    utility_appetite_tariff?: GridNodeEvidenceFactor;
    power_price?: GridNodeEvidenceFactor;
    incentives_community?: GridNodeEvidenceFactor;
    node_facts?: GridNodeEvidenceFact[];
    data_confidence?: string;
    improvement_hooks?: string[];
    site_profile?: GridSiteProfile;
  } | null;
  curated_at: string | null;
}

export interface ClusterTract {
  parcel_id: string;
  parcel_id_local: string;
  tract: string;
  acreage: number;
  avg_slope: number | null;
  pct_u5: number | null;
  on_bench: boolean;
  status: string | null;
  nearest_sub_kv: number | null;
  nearest_sub_miles: number | null;
  grid_total: number | null;
  developable_acres: number | null;
  buildability_pct: number | null;
  premium_flat_pct: number | null;
  red_flags: RedFlag[];
  in_core: boolean;
  in_available: boolean;
  geojson: string;
}

export interface ClusterAssembly {
  parcel_id: string;
  parcel_id_local: string;
  assembly_key: 'core' | 'available';
  acreage: number;
  avg_slope: number | null;
  developable_acres: number | null;
  buildability_pct: number | null;
  grid: GridScore | null;
  node: GridNodeIntel | null;
  nearest_sub_kv: number | null;
  nearest_sub_miles: number | null;
  member_parcel_ids: string[];
  geojson: string;
}

export interface ClusterSite {
  grid: GridScore | null;
  node: GridNodeIntel | null;
  dataConfidence: 'provisional' | 'partial' | 'firm';
  improvementHooks: string[];
}

export interface ClusterReport {
  cluster: {
    slug: string;
    name: string;
    county: string;
    state: string;
    centroid: [number, number];
    extent: [number, number, number, number];
  };
  site: ClusterSite | null;
  assemblies: ClusterAssembly[];
  tracts: ClusterTract[];
  benches: GeoJSON.FeatureCollection;
}

export interface CarveOutAggregate {
  carveOutId: string;
  slug: string;
  name: string;
  floodZonePct100yr: number | null;
  parcelCount: number;
  totalAcreage: number | null;
  totalLandValue: number | null;
  weightedAvgSlopePct: number | null;
  weightedPctUnder2Pct: number | null;
  weightedPctUnder5Pct: number | null;
  weightedPctUnder10Pct: number | null;
  weightedPctUnder15Pct: number | null;
  // Briefings with a slope file only: the weighted fields above hold the 3 m grid figure and
  // this the 5 m one, shown together as a range (see src/components/briefing/slopeRange.ts).
  slopeGrid5m?: { avgSlopePct: number; pctUnder2Pct: number; pctUnder5Pct: number; pctUnder10Pct: number; pctUnder15Pct: number };
  weightedWetlandPct: number | null;
  minPipelineDistanceMiles: number | null;
  maxPipelineAvailableMw: number | null;
}

export interface GasSupplyZoneCommitment {
  slug: string;
  operatorName: string;
  assetName: string;
  regionLabel: string;
  peakBcfd: number | null;
  peakYear: number | null;
  inventoryLocations: number | null;
  inventoryYears: number | null;
  dedicatedAcres: number | null;
  drillScheduleNotes: string | null;
  sourceFilename: string | null;
  sourceDate: string | null;
  zoneUrl: string;
  corridorTakeaway?: { pipelineCount: number; totalBcfd: number | null } | null;
}

export interface OperatorCarveOut {
  id: string;
  slug: string;
  name: string;
  ownerName: string;
  description: string | null;
  parcelIds: string[];
  representativeParcelId: string | null;
  totalAcres: number | null;
  centroidLat: number | null;
  centroidLng: number | null;
  subdivisionNote: string | null;
  positionLabel: string | null;
  floodZonePct100yr: number | null;
  geojson: GeoJSON.Geometry | null;
  commitmentSlug: string;
  operatorName: string;
}

export const fetchBriefingData = async (parcelId: string, token?: string, profile?: 'grid', carveOut?: string, layouts?: 'alt'): Promise<BriefingData> => {
  // Grid lens (?profile=grid), carve-out disambiguation (?carveOut=<slug>) and the
  // seller-alternatives edition (?layouts=alt, Crown Pine A1 only) are all opt-in.
  // When none is passed the request URL is byte-identical to the default
  // briefing — protected share links never pass them.
  const extra: string[] = [];
  if (profile === 'grid') extra.push('profile=grid');
  if (carveOut) extra.push(`carveOut=${encodeURIComponent(carveOut)}`);
  if (layouts === 'alt') extra.push('layouts=alt');
  // Token-based access uses plain fetch — the axios interceptor auto-adds
  // Authorization headers which would conflict with token-only auth.
  if (token) {
    const base = getApiUrl();
    const qs = ['token=' + encodeURIComponent(token), ...extra].join('&');
    const resp = await fetch(`${base}/briefing/${parcelId}?${qs}`);
    if (resp.status === 410) throw { response: { status: 410 } };
    if (!resp.ok) throw { response: { status: resp.status } };
    return resp.json();
  }
  const resp = await api.get(`/briefing/${parcelId}${extra.length ? `?${extra.join('&')}` : ''}`);
  return resp.data;
};

export const createShareLink = async (parcelId: string, expiresInDays = 30, label?: string, carveOutSlug?: string, profile?: 'grid', redaction?: 'partner', layouts?: 'alt'): Promise<{ url: string; token: string; expires_at: string; label: string | null; carve_out_slug: string | null; profile: string | null; redaction: 'partner' | null; layouts: 'alt' | null; redaction_rejected?: boolean }> => {
  const resp = await api.post(`/briefing/${parcelId}/share`, { expires_in_days: expiresInDays, label, carve_out_slug: carveOutSlug, profile, redaction, layouts });
  return resp.data;
};

// Panel supplement data — grades + nearby listings
export interface PanelListing {
  listing_title: string | null;
  price: number | null;
  acreage: number | null;
  seller_name: string | null;
  listing_url: string | null;
  landwatch_url: string | null;
  county: string | null;
  state: string;
  distance_miles: number;
}

export interface PanelSupplementData {
  grades: BriefingGrades | null;
  listings: PanelListing[];
}

const supplementCache = new Map<string, PanelSupplementData>();

export const fetchPanelSupplement = async (parcelId: string): Promise<PanelSupplementData> => {
  const cached = supplementCache.get(parcelId);
  if (cached) return cached;
  const resp = await api.get(`/grades/${parcelId}`);
  const raw = resp.data;
  const data: PanelSupplementData = raw && 'listings' in raw
    ? raw as PanelSupplementData
    : { grades: raw as BriefingGrades | null, listings: [] };
  supplementCache.set(parcelId, data);
  return data;
};

export const fetchClusterReport = async (slug: string): Promise<ClusterReport> => {
  const resp = await api.get(`/clusters/${slug}`);
  return resp.data;
};

// Owner-only: fetch the cluster's permanent share token (lazily minted server
// side). Used by the dossier masthead to build a tokenized "Share" URL.
export const fetchClusterShareToken = async (slug: string): Promise<string> => {
  const resp = await api.get(`/clusters/${slug}/share`);
  return resp.data.token;
};

export default api;
